import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
    IconButton, Link as MuiLink, ListItemText, Menu, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
    TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/admin/PageHeader';
import EmptyState from '../../components/admin/EmptyState';
import ZarizeniDialog from '../../components/iot/ZarizeniDialog';
import { formatNaposledy, formatPlnyCas, stavSpojeniEvidence, stitekSchvaleni, tvarPoctu } from '../../components/iot/iotStav';
import { useTed } from '../../components/iot/useTed';
import { useIotObjektyQuery } from '../../api/JsdhNotifikaceService/service/IotMutations';
import {
    useHledejZarizeniQuery, useKatalogQuery, useNezarazenaQuery, useNovaZarizeniQuery,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import {
    KATEGORIE, POVOLIT_PRIDANI_SIA, POVOLIT_PRIDANI_ZIGBEE, SCHVALENI, VYSVETLENI_SCHVALENI, ZATIM_JEN_SHELLY, type FiltrEvidence, type IotZarizeni, type IotZarizeniRequest, type KatalogModel,
    type NezarazenaSia, type NezarazenySenzor, type NoveZarizeni,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniService';

const VSE = '';

// Rozměry jako řetězce v px: číslo 1 v `sx` znamená 100 % a skrytý popisek by roztáhl stránku do šířky (D1).
const skryte = {
    border: 0, clip: 'rect(0 0 0 0)', height: '1px', margin: '-1px', overflow: 'hidden', padding: 0, position: 'absolute' as const,
    whiteSpace: 'nowrap', width: '1px',
};

type Zalozka = 'evidence' | 'nova' | 'nezarazena';
/** Pole Zobrazit (spec UX IoT 4.8): jedna volba místo tří přepínačů; Nedostupná filtruje FE podle `online` (K11a). */
type Zobrazit = '' | 'nedostupna' | 'neschvaleno' | 'neovereno' | 'nedoresena';
const ZOBRAZIT: { hodnota: Zobrazit; label: string }[] = [
    { hodnota: '', label: 'Vše' },
    { hodnota: 'nedostupna', label: 'Nedostupná' },
    { hodnota: 'neschvaleno', label: 'Neschválené správcem systému' },
    { hodnota: 'neovereno', label: 'Neověřený model' },
    { hodnota: 'nedoresena', label: 'Nedořešená identita' },
];

/** Dialog zařízení: přidání (s polem Objekt), přidání z fronty s předvyplněním, nebo úprava. */
type DialogStav =
    | { druh: 'nove'; klic: string; objektId: number; predvyplneni?: Partial<IotZarizeniRequest> }
    | { druh: 'uprava'; klic: string; zarizeni: IotZarizeni };

/** Shelly senzory zakládá gateway s prefixem shelly_ v zigbee_id (stejné pravidlo jako BE propojZEvidenci). */
const PREFIX_SHELLY = 'shelly_';
const idSenzoru = (s: NezarazenySenzor): string => {
    const id = s.zigbeeId ?? '';
    return id.startsWith(PREFIX_SHELLY) ? id.slice(PREFIX_SHELLY.length) : id;
};

const obsahuje = (hledat: string, ...pole: (string | null | undefined)[]): boolean => {
    const h = hledat.trim().toLocaleLowerCase('cs');
    return !h || pole.some((p) => !!p && p.toLocaleLowerCase('cs').includes(h));
};

/** Fronta Nová: NOVE nahoře, potom podle posledního kontaktu (nejnovější první). */
export const seradNova = (nova: NoveZarizeni[]): NoveZarizeni[] => [...nova].sort((a, b) =>
    (a.stav === 'NOVE' ? 0 : 1) - (b.stav === 'NOVE' ? 0 : 1) || (b.posledniKontaktEpochMs ?? 0) - (a.posledniKontaktEpochMs ?? 0));

const textNoveCeka = (n: number) => `${n} ${tvarPoctu(n, 'nové zařízení čeká', 'nová zařízení čekají', 'nových zařízení čeká')} na přidání do evidence`;

/** Věta pruhu K vyřízení (4.8); null = není co vyřizovat. */
export const textKVyrizeni = (noveCeka: number, senzory: number, ustredny: number): string | null => {
    const casti: string[] = [];
    if (noveCeka > 0) casti.push(textNoveCeka(noveCeka));
    const nezarazene = [
        senzory > 0 ? `${senzory} ${tvarPoctu(senzory, 'senzor', 'senzory', 'senzorů')}` : null,
        ustredny > 0 ? `${ustredny} ${tvarPoctu(ustredny, 'ústředna', 'ústředny', 'ústředen')}` : null,
    ].filter((x): x is string => !!x);
    if (nezarazene.length) {
        const jedenDruh = nezarazene.length === 1 ? (senzory || ustredny) : 0;
        const sloveso = jedenDruh === 1 || jedenDruh >= 5 ? 'nemá' : 'nemají';
        casti.push(`${nezarazene.join(' a ')} ${sloveso} záznam v evidenci`);
    }
    return casti.length ? `K vyřízení: ${casti.join(', ')}.` : null;
};

const nazevModeluKatalogu = (katalog: KatalogModel[], kod: string | null | undefined): string => {
    if (!kod) return '';
    const m = katalog.find((x) => x.kod === kod);
    return m ? `${m.vyrobce} ${m.nazev}` : kod;
};

/** Šedý podřádek pod názvem: výrobce a model · umístění · ID. */
export const podradekZarizeni = (z: IotZarizeni): string =>
    [[z.vyrobce, z.modelNazev].filter(Boolean).join(' '), z.umisteni, z.identifikator].filter(Boolean).join(' · ');

export const odkazDoObjektu = (z: Pick<IotZarizeni, 'id' | 'objektId'>) => `/admin/iot/objekty/${z.objektId}?zalozka=zarizeni#zarizeni-${z.id}`;

/**
 * Sloupec Stav: stav spojení podle 1.1 (K11a) a pod ním štítky 1.2, jen pokud nejsou normální.
 * Text s tooltipem a štítek s tooltipem jdou zaměřit klávesnicí.
 */
const StavZarizeni: React.FC<{ z: IotZarizeni; ted: number }> = ({ z, ted }) => {
    const spojeni = stavSpojeniEvidence(z, ted);
    const stitky = stitekSchvaleni(z);
    return (
        <Box>
            <Tooltip title={spojeni.tooltip ?? ''}>
                <Typography variant="body2" component="span" tabIndex={spojeni.tooltip ? 0 : undefined} data-testid={'evidence-spojeni-' + z.id}
                    color={spojeni.barva === 'warning' ? 'warning.main' : 'text.secondary'}>
                    {spojeni.text}
                </Typography>
            </Tooltip>
            {stitky.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {stitky.map((s) => (
                        <Tooltip key={s.klic} title={s.tooltip ?? ''}>
                            <Chip size="small" color={s.barva} variant={s.variant} label={s.text} tabIndex={s.tooltip ? 0 : undefined} />
                        </Tooltip>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const ChybaSeZnovu: React.FC<{ text: string; onZnovu: () => void }> = ({ text, onZnovu }) => (
    <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={onZnovu}>Zkusit znovu</Button>}>{text}</Alert>
);

/** Štítek záložky: jediné číslo je v textu a odpovídá položkám v záložce (E6), žádný druhý odznak. */
const StitekZalozky: React.FC<{ text: string; chyba?: boolean }> = ({ text, chyba }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {text}
        {chyba && (
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', display: 'inline-block' }}>
                <Box component="span" sx={skryte}>(nepodařilo se načíst)</Box>
            </Box>
        )}
    </Box>
);

const IoTEvidencePage: React.FC = () => {
    const theme = useTheme();
    const uzky = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [zalozka, setZalozka] = useState<Zalozka>('evidence');
    const [hledat, setHledat] = useState('');
    const [q, setQ] = useState('');
    const [objektId, setObjektId] = useState<number | ''>(VSE);
    const [kategorie, setKategorie] = useState('');
    const [zobrazit, setZobrazit] = useState<Zobrazit>('');
    const [dialog, setDialog] = useState<DialogStav | null>(null);
    const [menuZarizeni, setMenuZarizeni] = useState<{ el: HTMLElement; z: IotZarizeni } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setQ(hledat.trim()), 400);
        return () => clearTimeout(t);
    }, [hledat]);

    const filtr: FiltrEvidence = useMemo(() => {
        const f: FiltrEvidence = {};
        if (q) f.q = q;
        if (objektId !== VSE) f.objektId = objektId;
        if (kategorie) f.kategorie = kategorie;
        if (zobrazit && zobrazit !== 'nedostupna') f[zobrazit] = true;
        return f;
    }, [q, objektId, kategorie, zobrazit]);
    const filtrAktivni = !!(q || objektId !== VSE || kategorie || zobrazit);
    const zrusitFiltry = () => { setHledat(''); setQ(''); setObjektId(VSE); setKategorie(''); setZobrazit(''); };

    const { data: nalezena = [], isLoading, error, refetch } = useHledejZarizeniQuery(filtr);
    const ted = useTed();
    // BE nedostupnost nefiltruje: `online` už v odpovědi je, filtruje se na FE.
    const zarizeni = zobrazit === 'nedostupna' ? nalezena.filter((z) => z.online === false) : nalezena;
    const { data: objekty = [] } = useIotObjektyQuery();
    const { data: katalog = [] } = useKatalogQuery();
    const { data: novaVse = [], error: chybaNova, refetch: znovuNova } = useNovaZarizeniQuery();
    const { data: nezarazena, error: chybaNezarazena, refetch: znovuNezarazena } = useNezarazenaQuery();

    const nazevObjektu = (id: number) => objekty.find((o) => o.id === id)?.nazev ?? `Objekt ${id}`;
    const vObjektu = <T extends { objektId: number }>(x: T) => objektId === VSE || x.objektId === objektId;
    // Sloupec Objekt jen tehdy, když se v řádcích může lišit (4.8).
    const ukazObjekt = objekty.length > 1 && objektId === VSE;

    // Filtr Objekt platí pro všechny záložky, hledání ve frontách filtruje na FE podle ID a názvu.
    const novaObjektu = novaVse.filter(vObjektu);
    const nova = seradNova(novaObjektu.filter((n) => obsahuje(hledat, n.zarizeniId, n.zigbeeId, n.rozpoznanyModel, n.modelKod,
        nazevModeluKatalogu(katalog, n.rozpoznanyModel ?? n.modelKod))));
    const senzoryObjektu = (nezarazena?.senzory ?? []).filter(vObjektu);
    const siaObjektu = (nezarazena?.sia ?? []).filter(vObjektu);
    const senzory = senzoryObjektu.filter((s) => obsahuje(hledat, s.nazev, s.zigbeeId));
    const sia = siaObjektu.filter((s) => obsahuje(hledat, s.nazev, s.accountId));
    const noveCeka = novaObjektu.filter((n) => n.stav !== 'ZAMITNUTO').length;
    const kVyrizeni = textKVyrizeni(noveCeka, senzoryObjektu.length, siaObjektu.length);
    const idNovych = new Set(novaVse.flatMap((n) => [n.zarizeniId, n.zigbeeId].filter((x): x is string => !!x)));

    const otevritPrvniFrontu = () => setZalozka(noveCeka > 0 ? 'nova' : 'nezarazena');

    const pridatZarizeni = () => setDialog({
        druh: 'nove', klic: `nove-${Date.now()}`, objektId: objekty.length === 1 ? objekty[0].id : objektId === VSE ? 0 : objektId,
    });
    const pridatNove = (n: NoveZarizeni) => {
        const kod = [n.modelKod, n.rozpoznanyModel].find((k) => k && katalog.some((m) => m.kod === k)) ?? null;
        const m = katalog.find((x) => x.kod === kod);
        setDialog({
            druh: 'nove', objektId: n.objektId, klic: `nove-${n.objektId}-${n.zdroj}-${n.zarizeniId}`,
            predvyplneni: {
                objektId: n.objektId, protokol: n.zdroj === 'ZIGBEE' ? 'ZIGBEE' : 'SHELLY_MQTT', identifikator: n.zigbeeId || n.zarizeniId,
                ...(m ? { modelKod: m.kod, kategorie: m.kategorie, vyrobce: m.vyrobce, modelNazev: m.nazev } : {}),
            },
        });
    };
    const doplnitSenzor = (s: NezarazenySenzor) => {
        const shelly = !!s.zigbeeId?.startsWith(PREFIX_SHELLY);
        setDialog({
            druh: 'nove', objektId: s.objektId, klic: `senzor-${s.id}`,
            predvyplneni: {
                objektId: s.objektId, protokol: shelly ? 'SHELLY_MQTT' : 'ZIGBEE', identifikator: idSenzoru(s),
                kategorie: shelly ? 'DETEKTOR_KOURE' : 'ZIGBEE_SENZOR', nazev: s.nazev ?? '', senzorId: s.id,
            },
        });
    };
    const doplnitSia = (s: NezarazenaSia) => setDialog({
        druh: 'nove', objektId: s.objektId, klic: `sia-${s.id}`,
        predvyplneni: { objektId: s.objektId, protokol: 'SIA', identifikator: s.accountId, kategorie: 'SIA_USTREDNA', nazev: s.nazev ?? '', siaAccountId: s.id },
    });

    const tlacitkoMenu = (label: string, otevrene: boolean, onClick: (el: HTMLElement) => void) => (
        <IconButton size="small" aria-label={label} aria-haspopup="menu" aria-expanded={otevrene} onClick={(e) => onClick(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
        </IconButton>
    );

    // ----- Záložka V evidenci -----
    const prazdnaEvidence = () => {
        if (!filtrAktivni) {
            return (
                <EmptyState icon={<DevicesOtherIcon />} title="V evidenci zatím nejsou žádná zařízení."
                    description="Přidejte je tlačítkem Přidat zařízení, nebo je převezměte ze záložky Nová, jakmile se připojí k serveru." />
            );
        }
        const odkazNaNova = zobrazit === 'neschvaleno' && noveCeka > 0;
        return (
            <EmptyState icon={<DevicesOtherIcon />} title="Filtru neodpovídá žádné zařízení."
                description={odkazNaNova ? `${textNoveCeka(noveCeka)} v záložce Nová.` : undefined}
                action={(
                    <Stack direction="row" spacing={1} justifyContent="center">
                        {odkazNaNova && <Button variant="contained" onClick={() => setZalozka('nova')}>Otevřít záložku Nová</Button>}
                        <Button onClick={zrusitFiltry}>Zrušit filtry</Button>
                    </Stack>
                )} />
        );
    };

    const nazevSOdkazem = (z: IotZarizeni) => (
        <Box sx={{ minWidth: 0 }}>
            <MuiLink component={RouterLink} to={odkazDoObjektu(z)} sx={{ fontWeight: 500 }}>{z.nazev}</MuiLink>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{podradekZarizeni(z)}</Typography>
        </Box>
    );
    const menuZarizeniTlacitko = (z: IotZarizeni) =>
        tlacitkoMenu(`Další akce zařízení ${z.nazev}`, menuZarizeni?.z.id === z.id, (el) => setMenuZarizeni({ el, z }));

    const evidence = () => {
        if (error) return <ChybaSeZnovu text="Evidenci se nepodařilo načíst." onZnovu={() => refetch()} />;
        if (isLoading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
        if (!zarizeni.length) return prazdnaEvidence();
        if (uzky) {
            return (
                <Stack spacing={1} sx={{ p: 1 }} data-testid="evidence-karty">
                    {zarizeni.map((z) => (
                        <Card key={z.id} variant="outlined">
                            <CardContent sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', '&:last-child': { pb: 2 } }}>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    {nazevSOdkazem(z)}
                                    <Box sx={{ mt: 0.5 }}><StavZarizeni z={z} ted={ted} /></Box>
                                </Box>
                                {menuZarizeniTlacitko(z)}
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            );
        }
        return (
            <Table size="small" data-testid="evidence-tabulka">
                <TableHead>
                    <TableRow>
                        <TableCell>Název</TableCell>
                        {ukazObjekt && <TableCell>Objekt</TableCell>}
                        <TableCell>Druh</TableCell>
                        <TableCell>Stav</TableCell>
                        <TableCell padding="checkbox"><Box component="span" sx={skryte}>Akce</Box></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {zarizeni.map((z) => (
                        <TableRow key={z.id} hover>
                            <TableCell>{nazevSOdkazem(z)}</TableCell>
                            {ukazObjekt && <TableCell>{nazevObjektu(z.objektId)}</TableCell>}
                            <TableCell>{KATEGORIE[z.kategorie] ?? z.kategorie}</TableCell>
                            <TableCell><StavZarizeni z={z} ted={ted} /></TableCell>
                            <TableCell padding="checkbox">{menuZarizeniTlacitko(z)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    // ----- Záložka Nová -----
    const frontaNova = () => {
        if (chybaNova) {
            return <ChybaSeZnovu text="Seznam nových zařízení se nepodařilo načíst. Server pro zařízení teď neodpovídá, zkuste to za chvíli." onZnovu={() => znovuNova()} />;
        }
        return (
            <>
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 1 }}>
                    Zařízení, která se připojila k serveru, ale v evidenci jednotky zatím nejsou. Přidáním do evidence se zařízení neschvaluje, zařízení povoluje správce systému Hasiči Online.
                </Typography>
                {!nova.length ? (
                    novaObjektu.length
                        ? <EmptyState icon={<DevicesOtherIcon />} title="Hledání neodpovídá žádné nové zařízení." />
                        : <EmptyState icon={<DevicesOtherIcon />} title="Žádná nová zařízení."
                            description="Jakmile se nové zařízení Shelly nebo Zigbee připojí k serveru, objeví se tady." />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID zařízení</TableCell>
                                    {ukazObjekt && <TableCell>Objekt</TableCell>}
                                    <TableCell>Rozpoznaný model</TableCell>
                                    <TableCell>Naposledy</TableCell>
                                    <TableCell>Schválení správcem</TableCell>
                                    <TableCell align="right"><Box component="span" sx={skryte}>Akce</Box></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {nova.map((n) => {
                                    const sch = SCHVALENI[n.stav];
                                    const barva = sch?.barvaFronta ?? 'default';
                                    const tooltip = [n.posledniKontaktEpochMs != null ? `Naposledy ${formatPlnyCas(n.posledniKontaktEpochMs)}` : null,
                                        n.prvniKontaktEpochMs != null ? `První kontakt ${formatPlnyCas(n.prvniKontaktEpochMs)}` : null].filter(Boolean).join(' · ');
                                    return (
                                        <TableRow key={`${n.objektId}-${n.zdroj}-${n.zarizeniId}`}>
                                            <TableCell sx={{ overflowWrap: 'anywhere' }}>{n.zarizeniId}</TableCell>
                                            {ukazObjekt && <TableCell>{nazevObjektu(n.objektId)}</TableCell>}
                                            <TableCell>{nazevModeluKatalogu(katalog, n.rozpoznanyModel ?? n.modelKod)}</TableCell>
                                            <TableCell>
                                                <Tooltip title={tooltip}>
                                                    <Box component="span" tabIndex={tooltip ? 0 : undefined}>
                                                        {n.posledniKontaktEpochMs != null ? formatNaposledy(n.posledniKontaktEpochMs) : '—'}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {sch ? (
                                                    <Tooltip title={n.stav === 'SCHVALENO' ? '' : VYSVETLENI_SCHVALENI}>
                                                        <Chip size="small" label={sch.labelFronta} color={barva} variant={barva === 'default' ? 'outlined' : 'filled'}
                                                            tabIndex={n.stav === 'SCHVALENO' ? undefined : 0} />
                                                    </Tooltip>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {n.zdroj === 'ZIGBEE' && !POVOLIT_PRIDANI_ZIGBEE ? (
                                                    <Typography variant="caption" color="text.secondary">{ZATIM_JEN_SHELLY}</Typography>
                                                ) : (
                                                    <Button size="small" variant={n.stav === 'ZAMITNUTO' ? 'outlined' : 'contained'}
                                                        aria-label={`Přidat do evidence ${n.zarizeniId}`} onClick={() => pridatNove(n)}>
                                                        Přidat do evidence
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </>
        );
    };

    // ----- Záložka Nezařazená -----
    const frontaNezarazena = () => {
        if (chybaNezarazena) return <ChybaSeZnovu text="Seznam nezařazených senzorů a ústředen se nepodařilo načíst." onZnovu={() => znovuNezarazena()} />;
        const radek = (klic: string, druh: string, nazev: string | null, id: string, objekt: number, akce: React.ReactNode, duplicita: boolean) => (
            <TableRow key={klic}>
                <TableCell>{druh}</TableCell>
                <TableCell>
                    {nazev || <Typography variant="body2" color="text.secondary" component="span">{id}</Typography>}
                    {duplicita && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Stejné ID je i mezi novými zařízeními. Stačí ho přidat jednou.
                        </Typography>
                    )}
                </TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere' }}>{id}</TableCell>
                {ukazObjekt && <TableCell>{nazevObjektu(objekt)}</TableCell>}
                <TableCell align="right">{akce}</TableCell>
            </TableRow>
        );
        return (
            <>
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 1 }}>
                    Senzory a ústředny, které už systém zná, ale nemají záznam v evidenci. Doplněním záznamu se propojí.
                </Typography>
                {!senzory.length && !sia.length ? (
                    <EmptyState icon={<DevicesOtherIcon />} title={senzoryObjektu.length || siaObjektu.length
                        ? 'Hledání neodpovídá žádný senzor ani ústředna.' : 'Všechny senzory a ústředny mají záznam v evidenci.'} />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Druh</TableCell><TableCell>Název</TableCell><TableCell>ID</TableCell>
                                    {ukazObjekt && <TableCell>Objekt</TableCell>}
                                    <TableCell align="right"><Box component="span" sx={skryte}>Akce</Box></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {senzory.map((s) => radek(`s-${s.id}`, 'Senzor', s.nazev, s.zigbeeId ?? '', s.objektId,
                                    (POVOLIT_PRIDANI_ZIGBEE || s.zigbeeId?.startsWith(PREFIX_SHELLY)) ? (
                                        <Button size="small" aria-label={`Doplnit do evidence senzor ${s.nazev || s.zigbeeId || ''}`} onClick={() => doplnitSenzor(s)}>
                                            Doplnit do evidence
                                        </Button>
                                    ) : <Typography variant="caption" color="text.secondary">{ZATIM_JEN_SHELLY}</Typography>,
                                    idNovych.has(idSenzoru(s)) || (!!s.zigbeeId && idNovych.has(s.zigbeeId))))}
                                {sia.map((s) => radek(`a-${s.id}`, 'Ústředna EPS/EZS', s.nazev, s.accountId, s.objektId,
                                    POVOLIT_PRIDANI_SIA ? (
                                        <Button size="small" aria-label={`Doplnit do evidence ústředna ${s.accountId}`} onClick={() => doplnitSia(s)}>
                                            Doplnit do evidence
                                        </Button>
                                    ) : <Typography variant="caption" color="text.secondary">{ZATIM_JEN_SHELLY}</Typography>,
                                    false))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </>
        );
    };

    return (
        <Box>
            <PageHeader title="Evidence zařízení" subtitle="Všechna IoT zařízení jednotky napříč objekty"
                action={<Button variant="contained" disabled={!objekty.length} onClick={pridatZarizeni}>Přidat zařízení</Button>} />

            {kVyrizeni && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="k-vyrizeni"
                    action={<Button color="inherit" size="small" onClick={otevritPrvniFrontu}>Zobrazit</Button>}>
                    {kVyrizeni}
                </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField label="Hledat" size="small" value={hledat} onChange={(e) => setHledat(e.target.value)} sx={{ flex: 2 }}
                        placeholder="Název, umístění, model, ID, výrobní číslo" />
                    <TextField select label="Objekt" size="small" value={objektId} sx={{ flex: 1, minWidth: 160 }}
                        onChange={(e) => setObjektId(e.target.value === VSE ? VSE : Number(e.target.value))}>
                        <MenuItem value={VSE}>Všechny</MenuItem>
                        {objekty.map((o) => <MenuItem key={o.id} value={o.id}>{o.nazev}</MenuItem>)}
                    </TextField>
                    <TextField select label="Druh" size="small" value={kategorie} onChange={(e) => setKategorie(e.target.value)}
                        sx={{ flex: 1, minWidth: 160 }} disabled={zalozka !== 'evidence'}>
                        <MenuItem value="">Všechny</MenuItem>
                        {Object.entries(KATEGORIE).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                    <TextField select label="Zobrazit" size="small" value={zobrazit} onChange={(e) => setZobrazit(e.target.value as Zobrazit)}
                        sx={{ flex: 1, minWidth: 180 }} disabled={zalozka !== 'evidence'}>
                        {ZOBRAZIT.map((z) => <MenuItem key={z.hodnota} value={z.hodnota}>{z.label}</MenuItem>)}
                    </TextField>
                </Stack>
            </Paper>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={zalozka} onChange={(_, v: Zalozka) => setZalozka(v)} variant="scrollable" allowScrollButtonsMobile
                    sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab value="evidence" label={`V evidenci (${zarizeni.length})`} />
                    <Tab value="nova" label={<StitekZalozky text={chybaNova ? 'Nová' : `Nová (${nova.length})`} chyba={!!chybaNova} />} />
                    <Tab value="nezarazena" label={<StitekZalozky text={chybaNezarazena ? 'Nezařazená' : `Nezařazená (${senzory.length + sia.length})`} chyba={!!chybaNezarazena} />} />
                </Tabs>
                {zalozka === 'evidence' && evidence()}
                {zalozka === 'nova' && frontaNova()}
                {zalozka === 'nezarazena' && frontaNezarazena()}
            </Paper>

            <Menu anchorEl={menuZarizeni?.el} open={!!menuZarizeni} onClose={() => setMenuZarizeni(null)}>
                <MenuItem onClick={() => {
                    if (menuZarizeni) setDialog({ druh: 'uprava', klic: `uprava-${menuZarizeni.z.id}`, zarizeni: menuZarizeni.z });
                    setMenuZarizeni(null);
                }}><ListItemText>Upravit údaje</ListItemText></MenuItem>
                <MenuItem onClick={() => { if (menuZarizeni) navigate(odkazDoObjektu(menuZarizeni.z)); setMenuZarizeni(null); }}>
                    <ListItemText>Otevřít v objektu</ListItemText>
                </MenuItem>
            </Menu>

            {dialog?.druh === 'nove' && (
                <ZarizeniDialog key={dialog.klic} objektId={dialog.objektId} predvyplneni={dialog.predvyplneni}
                    objektNazev={dialog.objektId && (dialog.predvyplneni || objekty.length === 1) ? nazevObjektu(dialog.objektId) : undefined}
                    objekty={dialog.predvyplneni ? undefined : objekty} onClose={() => setDialog(null)} />
            )}
            {dialog?.druh === 'uprava' && (
                <ZarizeniDialog key={dialog.klic} objektId={dialog.zarizeni.objektId} zarizeni={dialog.zarizeni} onClose={() => setDialog(null)} />
            )}

        </Box>
    );
};

export default IoTEvidencePage;
